<template>
  <div>
    <div id="headline" class="pr-2 pr-md-3">
      <h1 class="pl-1">{{ getName }}</h1>
      <router-link
        class="headline-link"
        :to="{
            name: 'categorypage',
            params: {
              categoryName: createUrlString(currentCategoryName),
              scrollToTop: true
            },
          }"
      >
        <p
          class="mb-0 mr-3 icon-chevron-left"
          style="margin-bottom: 0 !important"
        >
          {{ $t("hellovue.maincontent.back_to_buildings") }}
        </p>
      </router-link>
    </div>
    <section
      class="content-holder d-flex flex-column-reverse flex-lg-row flex-lg-wrap"
    >
      <side-navigation-wide class="" />
      <side-navigation class="" />
      <transition name="fade-y" mode="out-in">
        <div
          v-if="streetViewStatus === $t('hellovue.view.street_view')"
          class="
            facade-image-container
            d-flex
            justify-content-between
            flex-wrap
          "
          key="facade"
        >
          <div id="facade-image-holder" class="col-12 col-lg-6">
            <img id="facade-image" :src="facadeImgLink" :alt="CutimgAlt" />
          </div>
          <div id="facade-text" class="col-12 col-lg-6">
            <h2>{{ getName }}</h2>
            <p v-html="getContentHTML"></p>

            <router-link
              class="button-like-link facade-view-link d-none d-lg-block"
              :to="{
                name: 'componentpage_view',
                params: {
                  componentName: this.$router.currentRoute.params.componentName,
                  view: $t('hellovue.view.cross_section_view'),
                },
              }"
            >
              <p
                class="mb-0 mr-3 icon-chevron-left"
                style="margin-bottom: 0 !important"
              >
                {{ $t("hellovue.maincontent.move_to_cross_section_view") }}
              </p>
            </router-link>
          </div>
        </div>

        <div
          v-else
          class="component-image-container d-flex align-items-center"
          :class="sideOpen"
          ref="componentImageContainer"
          key="cutImage"
        >
          <div
            id="component-image-holder"
            ondragstart="return false"
            :style="{ compImgContainerSize }"
            ref="componentImage"
            @mousedown="dragActive($event)"
            @mousemove="drag($event)"
          >
            <img
              v-for="highlight in getAllImgHighLight"
              :key="highlight.imageUrl"
              :src="highlight.imageUrl"
              alt="highlight.imageAlttext"
              class="highLightImg"
              :class="{ visible: highlight.imageClass === getActiveDataType }"
            />
            <!-- -->
            <img
              v-if="CutimgLink"
              @load="imgLoaded"
              id="component-image"
              :src="CutimgLink"
              :alt="CutimgAlt"
              rel="preload"
            />
            <svg
              v-if="loading"
              class="spinner"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
              ref="loadingSpinner"
            >
              <circle class="spinner-circle" cx="50" cy="50" r="45" />
            </svg>
            <map-button
              v-for="dataType in getDataByType(getActiveDataType)"
              class="map-btn"
              :button="dataType"
              :key="dataType.id"
            />
          </div>
        </div>
      </transition>
      <div
        v-if="streetViewStatus == $t('hellovue.view.cross_section_view')"
        class="d-flex align-content-stretch zoom-buttons"
      >
        <div class="zoom-button-col" style="width: 40px">
          <button
            @click="zoom(false, +0.25)"
            class="icon-plus mb-1"
            aria-label="zoom plus"
          ></button>
          <button
            @click="zoom(false, -0.25)"
            class="icon-minus"
            aria-label="zoom minus"
          ></button>
        </div>
        <div class="ml-1 zoom-button-col">
          <button
            class="h-100 icon-building"
            @click="zoom(true, 1)"
            aria-label="zoom"
          ></button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ComponentGalleryHeader from "src/components/partials/ComponentGalleryHeader.vue";
import MapButton from "src/components/partials/MapButton.vue";
import SideNavigation from "src/components/partials/SideNavigation.vue";
import SideNavigationWide from "src/components/partials/SideNavigationWide.vue";
import { createUrlString } from "src/store/helpers";
import { mapActions, mapGetters } from "vuex";
import { dragAndZoom } from "src/mixins/dragAndZoom.js";
import { i18n } from "src/translations/i18n";

export default {
  name: "ComponentPage",
  components: {
    ComponentGalleryHeader,
    MapButton,
    SideNavigation,
    SideNavigationWide,
  },
  mixins: [dragAndZoom],
  data() {
    return {
      imgPosX: 0,
      imgPosY: 0,
      loading: true,
      previousX: null,
      previousY: null,
    };
  },
  computed: {
    ...mapGetters([
      "getActiveDataType",
      "getName",
      "getContentHTML",
      "getDataByType",
      "getAllImgHighLight",
      "getCurrCutImage",
      "getCurrFacadeImage",
      "sideNavigationWideStatus",
      "streetViewStatus",
      "getZoomState",
      "getCategories",
    ]),
    CutimgLink: function () {
      if (this.getCurrCutImage) {
        return `${this.getCurrCutImage.imageUrl}`.replace(
          "&imagePreview=1",
          ""
        );
      } else {
        return "";
      }
    },
    facadeImgLink() {
      if (this.getCurrFacadeImage) {
        return this.getCurrFacadeImage.imageUrl;
      } else {
        return "";
      }
    },
    CutimgAlt: function () {
      if (this.getCurrCutImage) {
        return this.getCurrCutImage.imageAlttext;
      } else {
        return "";
      }
    },
    sideOpen: function () {
      if (this.sideNavigationWideStatus && window.innerWidth > 1024) {
        return "side-nav-wide-open";
      } else {
        return;
      }
    },
    compImgContainerSize: function () {
      if (document.querySelector(".component-image-container")) {
        return `width:${
          document.querySelector(".component-image-container").offsetWidth
        }px;height:${
          document.querySelector(".component-image-container").offsetHeight
        }px;`;
      } else {
        return;
      }
    },
    currentCategory: function () {
      return this.getCategories.find((cat) => createUrlString(cat.name) == this.$route.params.categoryName)
    },
    currentCategoryId: function () {
      if (this.currentCategory && this.currentCategory.id) return this.currentCategory.id;
      return undefined;
    },
    currentCategoryName: function () {
      if (this.currentCategory && this.currentCategory.name) return this.currentCategory.name;
      return undefined;
    },
  },
  mounted() {
    let streetViewName = i18n.messages[i18n.locale].hellovue.view.street_view;
    this.setStreetView(streetViewName);
    this.fitScreenSize();
  },
  methods: {
    ...mapActions(["initComponentPageData", "setStreetView"]),
    createUrlString,
    fitScreenSize() {
      const content = document.querySelector(".component-image-container");
      const sideNav = document.querySelector(".side-nav");
      let qaBanner = 0;
      if (document.querySelector("#ControlMenu")) {
        qaBanner = document.querySelector("#ControlMenu").offsetHeight;
      }

      if (window.innerWidth > 1024) {
        sideNav.style.height = `${
          window.visualViewport.height -
          document.querySelector("#component-gallery-header").offsetHeight -
          qaBanner -
          50
        }px`;
      }
      window.addEventListener("resize", function () {
        const content = document.querySelector(".component-image-container");

        if (window.innerWidth > 1024) {
          content.style.height = `${
            window.visualViewport.height -
            document.querySelector("#component-gallery-header").offsetHeight -
            qaBanner
          }px`;

          sideNav.style.height = `${
            window.visualViewport.height -
            document.querySelector("#component-gallery-header").offsetHeight -
            document.querySelector("#headline").offsetHeight -
            qaBanner -
            50
          }px`;
        } else {
          content.style.height = "unset";
        }
      });
    },
    imgLoaded: function () {
      this.loading = false;
    },
    zoomMouse: function () {
      let self = this;

      this.$nextTick(() => {
        document
          .querySelector(".component-image-container")
          .addEventListener("wheel", function (e) {
            e.preventDefault();
            self.$refs.componentImage.style.transformOrigin = null;

            const img = self.$refs.componentImage;
            const imgContainer = document.querySelector(
              ".component-image-container"
            );

            // use wheel delta to calculate zoom direction. Cap zoom to avoid crazy numbers
            let delta = e.delta || e.wheelDelta;
            if (delta === undefined) {
              delta = e.originalEvent.detail;
            }
            delta = Math.max(-1, Math.min(1, delta));

            let offset = {
              x: imgContainer.scrollLeft,
              y: imgContainer.scrollTop,
            };

            self.imgPosX =
              window.event.clientX -
              img.offsetLeft +
              imgContainer.getBoundingClientRect().left;
            self.imgPosY =
              window.event.clientY -
              img.offsetTop +
              imgContainer.getBoundingClientRect().top;

            const image_loc = {
              x: e.pageX + offset.x,
              y: e.pageY + offset.y,
            };
            const zoom_point = {
              x: image_loc.x / self.zoomState,
              y: image_loc.y / self.zoomState,
            };

            //  zoomm
            self.zoomState += delta * 0.25 * self.zoomState;
            self.zoomState = Math.max(
              1,
              Math.min(self.maxScale, self.zoomState)
            );

            const zoom_point_new = {
              x: zoom_point.x * self.zoomState,
              y: zoom_point.y * self.zoomState,
            };

            const newScroll = {
              x: zoom_point_new.x - e.pageX,
              y: zoom_point_new.y - e.pageY,
            };

            self.$refs.componentImage.style.transform = `scale(${self.zoomState})`;
            imgContainer.scrollTop = newScroll.y;
            imgContainer.scrollLeft = newScroll.x;
          });
      });
    },

    /**
     * DRAGGING
     */
    //get mouse coordinates (if mouse dragging)
    dragActive: function (e) {
      this.previousX = e.pageX;
      this.previousY = e.pageY;
    },
    drag: function (e) {
      if (e.buttons) {
        let dragX = 0;
        let dragY = 0;
        // skip the drag when the x position was not changed
        if (e.pageX - this.previousX !== 0) {
          dragX = this.previousX - e.pageX;
          this.previousX = e.pageX;
        }
        // skip the drag when the y position was not changed
        if (e.pageY - this.previousY !== 0) {
          dragY = this.previousY - e.pageY;
          this.previousY = e.pageY;
        }
        // scrollBy x and y
        if (dragX !== 0 || dragY !== 0) {
          document
            .querySelector(".component-image-container")
            .scrollBy(dragX, dragY);
        }
      }
    },
  },
  updated() {
    setTimeout(() => {
      if (document.querySelector(".component-image-container")) {
        this.zoomMouse();
      }
    }, 1000);
  },
  watch: {
    streetViewStatus: {
      handler(newValue) {
        if (newValue === this.$t("hellovue.view.street_view")) {
          this.loading = true;
        }
        document.querySelector("#component-gallery-content").scrollTop = 0;
      },
    },
  },
};
</script>

<style lang="scss">
#headline {
  display: flex;
  justify-content: space-between;
}

.headline-link {
  align-items: center;
  display: flex;
  line-height: 1.5rem;
  height: 1.5rem;
  margin-top: 1.5rem;
}

aside {
  overflow: auto;
}
.content-holder {
  position: relative;
}

.facade-image-container {
  transition: all 0.3s;
  overflow: auto;
  #facade-image {
    object-fit: contain;
    width: 100%;
  }
  @media (min-width: 1024px) {
    width: 75%;
  }
}

.component-image-container {
  overflow: hidden;
  transition: all 0.3s;
  #component-image-holder {
    position: relative;
    width: 100%;
    object-fit: contain;
    transform-origin: 0 0;

    cursor: move;
    img {
      height: 100%;
      object-fit: contain;
      pointer-events: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -o-user-select: none;
      user-select: none;
      width: 100%;
    }
    .highLightImg {
      opacity: 0;
      position: absolute;
      transition: all 0.3s;
      object-fit: contain;
      width: 100%;
      height: 100%;
      &.visible {
        opacity: 0.85;
      }
    }
  }
  @media (min-width: 1024px) {
    width: 75%;
  }
  &.side-nav-wide-open {
    width: 65%;
  }
  @media (max-width: 1023px) {
    width: 100%;
  }
}
.map-btn {
  position: absolute;
  transform-origin: 50%, 50%;
  background: none;
  border: none;
  cursor: pointer;
  transition: 0.3s;
  width: 80px;
  height: 80px;
  transform: translate(-40px, -40px);
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .btn-svg {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: white;
    filter: blur(10px);
    mix-blend-mode: overlay;
    opacity: 0;
    position: absolute;
    transform-origin: 50%, 50%;
    left: 0;
    top: 0;
    transition: opacity 0.2s;
  }

  &.activeBtn {
    .btn-svg {
      opacity: 0.8;
    }
  }
  &:hover {
    .btn-svg {
      opacity: 0.8;
    }
    .mapPinSvg:hover {
      .R-luokka {
        fill: #ea6708;
      }
      .L-luokka {
        fill: #ea6708;
      }
      .S-luokka {
        fill: #ea6708;
      }
    }
  }
}
.fade-y-enter,
.fade-y-leave-to {
  opacity: 0;
}

.fade-y-enter-active,
.fade-y-leave-active {
  transition-delay: 0.4s;
  transition: opacity 0.3s;
}

// zoom
.zoom-buttons {
  position: absolute;
  right: 20px;
  top: 20px;

  @media (max-width: 1024px) {
    right: 20px;
    top: 20px;
  }
  .zoom-button-col {
    height: 85px;
    width: 40px;
  }
  button {
    width: 40px;
    height: 40px;
    background: white;
    border: #002f6c 1px solid;
    color: #002f6c;
    transition: 0.3s;
    &:hover {
      background: #002f6c;
      border: white;
      color: white;
    }
  }
}

.spinner {
  display: block;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  shape-rendering: auto;
  position: absolute;
  animation: 2s linear infinite svg-animation;
  max-width: 100px;
}

@keyframes svg-animation {
  0% {
    transform: rotateZ(0deg);
  }
  100% {
    transform: rotateZ(360deg);
  }
}

.spinner-circle {
  animation: 1.4s ease-in-out infinite both circle-animation;
  display: block;
  fill: transparent;
  stroke: #00a9e0;
  stroke-linecap: round;
  stroke-dasharray: 283;
  stroke-dashoffset: 280;
  stroke-width: 10px;
  transform-origin: 50% 50%;
}

@keyframes circle-animation {
  0%,
  25% {
    stroke-dashoffset: 280;
    transform: rotate(0);
  }

  50%,
  75% {
    stroke-dashoffset: 75;
    transform: rotate(45deg);
  }

  100% {
    stroke-dashoffset: 280;
    transform: rotate(360deg);
  }
}
</style>
